import { useCallback, useEffect, useRef } from 'react';

import { useTourGuide } from '@/tour-guide';
import { WORKSPACES_TOUR_ID, WORKSPACES_INTRO_STEP_ID } from '@/tour-guide/tours';
import { HOST, WORKSPACE_MENU, resolveAnchor } from '@/tour-guide/engine/anchors';
import {
  closeHoverMenu,
  holdMenuOpen,
  openHoverMenu,
  type HoverMenu,
} from '@/tour-guide/tasks/hoverMenu';

const MENU: HoverMenu = {
  name: 'workspaces',
  trigger: HOST.workspaceSwitcher,
  panel: WORKSPACE_MENU,
};

function setPanelVisible(visible: boolean): void {
  const panel = resolveAnchor(WORKSPACE_MENU)?.element;
  if (!panel) return;
  panel.style.transition = 'opacity 120ms ease';
  panel.style.opacity = visible ? '1' : '0';
}

export const TOUR_START_GRACE_MS = 3000;

export interface TourRunnerProps {
  requested: boolean;
  onFinished: () => void;
}

export function TourRunner({ requested, onFinished }: TourRunnerProps) {
  const { start, run } = useTourGuide();
  const startedRef = useRef(false);
  const sawRunningRef = useRef(false);
  const finishedRef = useRef(false);
  const releaseHoldRef = useRef<(() => void) | null>(null);

  const finishRef = useRef(onFinished);
  finishRef.current = onFinished;

  const finishOnce = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    finishRef.current();
  };

  const restoreSidebar = useCallback(async () => {
    setPanelVisible(true);
    releaseHoldRef.current?.();
    releaseHoldRef.current = null;
    await closeHoverMenu(MENU);
  }, []);

  useEffect(() => {
    if (!requested || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    const frame = window.requestAnimationFrame(() => {
      void (async () => {
        releaseHoldRef.current?.();
        releaseHoldRef.current = holdMenuOpen(MENU);
        const opened = await openHoverMenu(MENU);
        if (cancelled) return;
        if (!opened) {
          console.warn(
            '[tour-guide] workspaces intro: the switcher panel never opened. Stops 2-5 ' +
              'will resolve nothing and be skipped.',
          );
        }
        setPanelVisible(false);
        start(WORKSPACES_TOUR_ID);
      })();
    });

    const bail = window.setTimeout(() => {
      if (!sawRunningRef.current) finishOnce();
    }, TOUR_START_GRACE_MS);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(bail);
    };
  }, [requested, start]);

  useEffect(() => {
    if (run.running) {
      sawRunningRef.current = true;
      setPanelVisible(run.stepId !== WORKSPACES_INTRO_STEP_ID);
      return;
    }
    if (sawRunningRef.current) {
      void restoreSidebar();
      finishOnce();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.running, run.stepId]);

  useEffect(() => () => void restoreSidebar(), [restoreSidebar]);

  return null;
}

export default TourRunner;
